'use client'

import { useContext, useRef, type CSSProperties } from 'react'
import { AnnouncementContext } from './context'
import type { Politeness } from './state'

// How a screen reader hears this app's status messages (WCAG 4.1.3).
//
// A screen reader reads what has focus and says nothing about a part of the page that
// changed elsewhere. The exception is a "live region": an element carrying `aria-live`,
// which the screen reader watches and reads aloud whenever the text inside it changes,
// without moving focus. This component renders those elements, and every announce() call
// becomes a text change inside one of them.
//
// Two rules of live regions shape everything below:
// 1. Only a *change* is spoken. Setting the same text twice is silent, and a region that
//    appears with text already in it is usually silent too.
// 2. The region must stay in the accessibility tree. `display: none` and
//    `visibility: hidden` remove it, so it is hidden the way screen-reader-only text always
//    is: out of the page flow, 1px, clipped — the same recipe as MUI's `visuallyHidden`.
const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

// Two identical regions used alternately: message N goes into one, message N+1 into the
// other while the first is emptied, so every announcement is a text change even when the
// same sentence repeats (rule 1).
function AlternatingLiveRegions({
  politeness,
  text,
  slot,
}: {
  politeness: Politeness
  text: string
  slot: number
}) {
  // role="status" implies aria-live="polite" and role="alert" implies "assertive"; both are
  // set because screen readers differ in which one they honour.
  const role = politeness === 'assertive' ? 'alert' : 'status'
  return (
    <>
      <div role={role} aria-live={politeness} aria-atomic>
        {slot === 0 ? text : ''}
      </div>
      <div role={role} aria-live={politeness} aria-atomic>
        {slot === 1 ? text : ''}
      </div>
    </>
  )
}

// Mount one at the root and one inside any MUI modal that announces: MUI marks every
// sibling of an open modal aria-hidden, which silences the root instance while it is open.
// Clerk's user-profile modal does not do this, so pages hosted there need nothing extra.
export function Announcer() {
  const { text, politeness, sequence } = useContext(AnnouncementContext)
  // An instance that appears inside a modal must not repeat the last message from before it
  // opened, so only announcements made after mount are rendered.
  const mountSequence = useRef(sequence)
  const current = sequence === mountSequence.current ? '' : text
  const slot = sequence % 2

  return (
    <div style={visuallyHidden}>
      <AlternatingLiveRegions
        politeness="polite"
        text={politeness === 'polite' ? current : ''}
        slot={slot}
      />
      <AlternatingLiveRegions
        politeness="assertive"
        text={politeness === 'assertive' ? current : ''}
        slot={slot}
      />
    </div>
  )
}
